import "@styles/styles.scss";
import { lazy, useContext, useEffect, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import Content from "./Content";
import UnauthenticatedContent from "./UnauthenticatedContent";
import ItiLoading from "./iti-core-react/components/ItiLoading/ItiLoading";
import {
	AuthContext,
	AuthProvider,
	User,
} from "./iti-core-react/contexts/auth";
import { NavigationProvider } from "./iti-core-react/contexts/navigation";
import { ItiHttpService } from "./iti-core-react/services/iti-http-service";
import { ItiLoadingService } from "./iti-core-react/services/iti-loading-service";
import { ItiTokenService } from "./iti-core-react/services/iti-token-service";
import { LegalConditionsPopupLogin } from "./pages/LoginForm/Popup/LegalConditionsPopupLogin";
import Style from "./pages/Style/Style";
import { API } from "./shared/api";
import { ThemeConfigProvider } from "./shared/contexts/ThemeConfigContext";
import { InfoRoute } from "./shared/dtos/info-route-dto";
import { ProfileDto } from "./shared/dtos/profile-dto";
import { UserStatus } from "./shared/enums/user-status";
import { UserService } from "./shared/services/userService";
import { useScreenSizeClass } from "./utils/media-query";
const InvitationForm = lazy(() => import("@/pages/Invitation/Invitation"));

export function isDevEnvironment() {
	const url = window.location.hostname.toLowerCase();
	return url === "localhost" || url.includes("dev");
}

function App() {
	const { user, loading, signOut, setUser, setProfile } =
		useContext(AuthContext);
	const isInIframe = window.self !== window.top;
	const match = useMatch("/invitation/:guid");
	const location = useLocation();
	const [showHeaderAndMenu, setOnlyHeader] = useState(true);
	const [showLegalConditions, setShowLegalConditions] = useState(false);
	const [checkingUserStatus, setCheckingUserStatus] = useState(false);

	// Decide que layout mostrar dependiendo de la ruta
	useEffect(() => {
		setOnlyHeader(
			location.pathname.startsWith("/experimentation/VSCodeViewer"),
		);
	}, [location]);

	// Verificar el estado del usuario cuando se autentica
	useEffect(() => {
		if (user && !checkingUserStatus) {
			setCheckingUserStatus(true);

			UserService.getCurrentUser()
				.then((currentUser) => {
					if (currentUser.userStatus === UserStatus.REGISTERED) {
						setShowLegalConditions(true);
					}
				})
				.finally(() => {
					setCheckingUserStatus(false);
				});
		}
	}, [user]);

	// Activar mostrar errores de las llamadas al API
	ItiHttpService.enableShowErrorMessage(true);

	useEffect(() => {
		// console.log(`INFO ABOUT AITANA INTEGRATION - isInIframe: ${isInIframe}`);
		// Si es un iframe

		if (isInIframe) {
			const handleMessage = (event: any) => {
				// Verifica el origen del mensaje
				//if (event.origin !== "http://example.com")
				// Reemplaza 'http://example.com' con el origen esperado
				//  return;

				// Actualiza el localStorage
				// localStorage.setItem(event.data.key, event.data.value);
				const message = event.data;

				ItiTokenService.setToken(
					message.access_token,
					message.refresh_token,
					message.expires_in,
					message.refresh_expires_in,
				).then(() => {
					if (!ItiTokenService.isTokenExpired()) {
						ItiHttpService.composeAuthHeader();
						// console.log("Login info: ");
						// console.log(message);

						UserService.getCurrentUser().then((currentUser: ProfileDto) => {
							const userStatus = currentUser.userStatus;

							if (userStatus === UserStatus.ACTIVE) {
								setProfile?.(currentUser);
							} else {
								signOut?.();
							}
						});

						if (setUser && setProfile) {
							setUser(new User(message.userName));
						}
					} else {
						window.parent.postMessage({ error: 401 }, "*");
					}
				});
			};

			window.addEventListener("message", handleMessage);

			return () => {
				window.removeEventListener("message", handleMessage);
			};
		}
	}, [isInIframe, setProfile, setUser, signOut]);

	// Efecto para manejar el cierre de la ventana
	useEffect(() => {
		const handleBeforeUnload = () => {
			const deploymentId = sessionStorage.getItem("deploymentId");
			const experimentId = sessionStorage.getItem("experimentId");
			const baseUrl = `${API.experimentation.experiments}`;
			const url = `${baseUrl}/${experimentId}/stop/${deploymentId}`;

			const data = JSON.stringify({});

			// Usar sendBeacon para asegurar que la petición se envía
			navigator.sendBeacon(url, data);
		};

		// Añadir el event listener para beforeunload
		window.addEventListener("beforeunload", handleBeforeUnload);

		// Cleanup: remover el event listener
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	// Componente de pruebas de estilos
	if (window.location.pathname === "/style") {
		return <Style />;
	}

	if (match?.pattern.path === "/invitation/:guid") {
		ItiTokenService.clearToken();
		return <InvitationForm infoRoute={match as InfoRoute} />;
	}

	if (loading || checkingUserStatus) {
		return <ItiLoading />;
	}

	if (user) {
		if (signOut) {
			ItiHttpService.setInterceptor(signOut);
		}

		if (showLegalConditions) {
			return (
				<div className="app-container">
					<LegalConditionsPopupLogin
						onSubmit={() => {
							setShowLegalConditions(false);
						}}
						onHideLegalConditionsPopUp={() => {
							setShowLegalConditions(false);
							signOut?.();
						}}
					/>
				</div>
			);
		}

		return <Content showHeaderAndMenu={showHeaderAndMenu} />;
	}

	return <UnauthenticatedContent />;
}

export default function Root() {
	const screenSizeClass = useScreenSizeClass();
	const [loading, setLoading] = useState(false);

	ItiLoadingService.setLoadingHandler(setLoading);

	return (
		<ThemeConfigProvider>
			<AuthProvider>
				<NavigationProvider>
					<div className={`app ${screenSizeClass}`}>
						{loading && <ItiLoading />}
						<App />
					</div>
				</NavigationProvider>
			</AuthProvider>
		</ThemeConfigProvider>
	);
}
